import { getIdForSlug } from '~/data/pokedex'
import spriteOverrides from '../../../shared/pokemon-sprite-overrides.json'

const SHOWDOWN_BASE = 'https://play.pokemonshowdown.com/sprites'

/** Sprites PNG TyraDex — dernier recours uniquement. */
const TYRA_DEX_BASE = 'https://raw.githubusercontent.com/Yarkis01/TyraDex/images/sprites'

const POKEAPI_SPRITE = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon'

/** Seules les espèces canoniques (1..1025) et formes officielles ont une correspondance d'ID dans PokéAPI. */
export function isOfficialPokeApiId(pokedexId: number): boolean {
  if (pokedexId >= 1 && pokedexId <= 1025) return true
  if (pokedexId >= 10001 && pokedexId <= 10048) return true
  if (pokedexId >= 10091 && pokedexId <= 10108) return true
  if (pokedexId >= 10110 && pokedexId <= 10128) return true
  if (pokedexId >= 10130 && pokedexId <= 10145) return true
  if (pokedexId >= 10150 && pokedexId <= 10153) return true
  return false
}

const REGION_SUFFIXES = new Set(['hisui', 'alola', 'galar', 'paldea'])

const TAUROS_PALDEA_TYRA_VARIANT: Record<string, string> = {
  'tauros-paldea': 'paldea',
  'tauros-paldeablaze': 'paldeablaze',
  'tauros-paldeaaqua': 'paldeaaqua',
}

const SPECIAL_SLUGS: Record<string, string> = {
  "Nidoran♀": 'nidoranf',
  "Nidoran♂": 'nidoranm',
  "Mr. Mime": 'mr-mime',
  "Mr. Rime": 'mr-rime',
  "Farfetch'd": 'farfetchd',
  "Sirfetch'd": 'sirfetchd',
  "Ho-Oh": 'hooh',
  "Mime Jr.": 'mime-jr',
  "Porygon-Z": 'porygon-z',
  "Flabébé": 'flabebe',
}

const SLUG_FIXES: Record<string, string> = {
  'ho-oh': 'hooh',
  'nidoran-f': 'nidoranf',
  'nidoran-m': 'nidoranm',
  'mr-mime': 'mrmime',
  'mime-jr': 'mimejr',
  'mr-rime': 'mrrime',
  'tauros-paldea': 'tauros-paldeacombat',
  'urshifu-rapid-strike': 'urshifu-rapidstrike',
  'urshifu-rapid-strike-gmax': 'urshifu-rapidstrike-gmax',
  'larry-e4': 'larry',
  wake: 'crasherwake',
}

/** Dossiers locaux dont le nom diffère du slug jeu (volume `/sprites/pokemon/`). */
const LOCAL_SPRITE_FOLDERS: Record<string, string> = {
  'ultra-necrozma': 'necrozma-ultra',
  terapagos: 'terapagos-normal',
}

type SpriteOverride = {
  regular?: string
  shiny?: string
  staticRegular?: string
  staticShiny?: string
}

const SPRITE_OVERRIDES = (
  spriteOverrides as {
    sprites?: Record<string, SpriteOverride>
  }
).sprites ?? {}

/** Phases d’affichage — ordre de fallback dans `PokemonSprite.vue`. */
export type SpriteDisplayPhase =
  | 'showdown-ani'
  | 'local-showdown-ani'
  | 'local-pokemon-ani'
  | 'override-ani'
  | 'showdown-static'
  | 'local-pokemon-static'
  | 'override-static'
  | 'tyradex'
  | 'pokeapi'
  | 'none'

export function getInitialSpritePhase(preference: 'animated' | 'light'): SpriteDisplayPhase {
  return preference === 'light' ? 'showdown-static' : 'showdown-ani'
}

export function fixSlug(slug: string): string {
  return SLUG_FIXES[slug] ?? slug
}

function localSpriteFolder(slug: string): string {
  return LOCAL_SPRITE_FOLDERS[slug] ?? LOCAL_SPRITE_FOLDERS[fixSlug(slug)] ?? slug
}

function getSpriteOverrideEntry(slug: string): SpriteOverride | undefined {
  return SPRITE_OVERRIDES[slug] ?? SPRITE_OVERRIDES[fixSlug(slug)]
}

function isAnimatedSpriteUrl(url: string): boolean {
  return /\.(gif|webp)($|\?)/i.test(url)
}

export function getShowdownAnimatedUrl(slug: string, shiny = false): string {
  const folder = shiny ? 'ani-shiny' : 'ani'
  return `${SHOWDOWN_BASE}/${folder}/${fixSlug(slug)}.gif`
}

export function getShowdownStaticUrl(slug: string, shiny = false): string {
  const folder = shiny ? 'dex-shiny' : 'dex'
  return `${SHOWDOWN_BASE}/${folder}/${fixSlug(slug)}.png`
}

/** Volume prod : `/sprites/pokemon-showdown/{slug}/anim_*.webp` */
export function getLocalShowdownAnimatedUrl(slug: string, shiny = false): string {
  const file = shiny ? 'anim_shiny.webp' : 'anim_normal.webp'
  return `/sprites/pokemon-showdown/${slug}/${file}`
}

export function getLocalShowdownStaticUrl(slug: string, shiny = false): string {
  const file = shiny ? 'static_shiny.webp' : 'static_normal.webp'
  return `/sprites/pokemon-showdown/${slug}/${file}`
}

export function getLocalPokemonAnimatedUrl(slug: string, shiny = false): string {
  const file = shiny ? 'anim_shiny.gif' : 'anim_normal.gif'
  return `/sprites/pokemon/${localSpriteFolder(slug)}/${file}`
}

export function getLocalPokemonStaticUrl(slug: string, shiny = false): string {
  const file = shiny ? 'static_shiny.png' : 'static_normal.png'
  return `/sprites/pokemon/${localSpriteFolder(slug)}/${file}`
}

function getOverrideAnimatedUrl(slug: string, shiny = false): string {
  const override = getSpriteOverrideEntry(slug)
  if (!override) return ''
  const url = shiny ? override.shiny ?? override.regular : override.regular
  if (!url || !isAnimatedSpriteUrl(url)) return ''
  return url
}

function getOverrideStaticUrl(slug: string, shiny = false): string {
  const override = getSpriteOverrideEntry(slug)
  if (!override) return ''
  if (shiny) {
    const s = override.staticShiny ?? override.staticRegular
    if (s) return s
    const r = override.shiny ?? override.regular ?? ''
    if (r && !isAnimatedSpriteUrl(r)) return r
    return ''
  }
  const s = override.staticRegular
  if (s) return s
  const r = override.regular ?? ''
  if (r && !isAnimatedSpriteUrl(r)) return r
  return ''
}

/** Overrides distants (PokéAPI, etc.) — les chemins `/sprites/pokemon/` passent par les phases locales. */
function isRemoteSpriteUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://')
}

export function hasOverrideAnimatedUrl(slug: string): boolean {
  const url = getOverrideAnimatedUrl(slug) || getOverrideAnimatedUrl(slug, true)
  return isRemoteSpriteUrl(url)
}

export function hasOverrideStaticUrl(slug: string): boolean {
  const url = getOverrideStaticUrl(slug) || getOverrideStaticUrl(slug, true)
  return isRemoteSpriteUrl(url)
}

export function getSpritePhaseUrl(
  phase: SpriteDisplayPhase,
  slug: string,
  shiny: boolean,
  pokedexId: number,
): string {
  switch (phase) {
    case 'showdown-ani':
      return getShowdownAnimatedUrl(slug, shiny)
    case 'local-showdown-ani':
      return getLocalShowdownAnimatedUrl(slug, shiny)
    case 'local-pokemon-ani':
      return getLocalPokemonAnimatedUrl(slug, shiny)
    case 'override-ani':
      return getOverrideAnimatedUrl(slug, shiny)
    case 'showdown-static':
      return getShowdownStaticUrl(slug, shiny)
    case 'local-pokemon-static':
      return getLocalPokemonStaticUrl(slug, shiny)
    case 'override-static':
      return getOverrideStaticUrl(slug, shiny)
    case 'tyradex':
      return getTyraDexSpriteUrlForSlug(slug, shiny)
    case 'pokeapi':
      return pokedexId > 0 && isOfficialPokeApiId(pokedexId) ? getPokeApiSpriteUrl(pokedexId, shiny) : ''
    default:
      return ''
  }
}

export function getNextSpritePhase(
  current: SpriteDisplayPhase,
  slug: string,
  preference: 'animated' | 'light',
): SpriteDisplayPhase {
  const withOverrideAni = hasOverrideAnimatedUrl(slug)
  const withOverrideStatic = hasOverrideStaticUrl(slug)
  const canFallbackPokeApi = isOfficialPokeApiId(getIdForSlug(slug))

  const fromAnimated: SpriteDisplayPhase[] = [
    'showdown-ani',
    'local-showdown-ani',
    'local-pokemon-ani',
    ...(withOverrideAni ? (['override-ani'] as const) : []),
    'showdown-static',
    'local-pokemon-static',
    ...(withOverrideStatic ? (['override-static'] as const) : []),
    'tyradex',
    ...(canFallbackPokeApi ? (['pokeapi'] as const) : []),
    'none',
  ]

  const fromLight: SpriteDisplayPhase[] = [
    'showdown-static',
    'local-pokemon-static',
    ...(withOverrideStatic ? (['override-static'] as const) : []),
    'tyradex',
    ...(canFallbackPokeApi ? (['pokeapi'] as const) : []),
    'none',
  ]

  const chain = preference === 'light' ? fromLight : fromAnimated
  const idx = chain.indexOf(current)
  if (idx < 0 || idx >= chain.length - 1) return 'none'
  return chain[idx + 1]!
}

/** Première URL animée (Showdown) — compat. appels directs. */
export function getSpriteUrl(slug: string): string {
  return getShowdownAnimatedUrl(slug)
}

export function getShinySpriteUrl(slug: string): string {
  return getShowdownAnimatedUrl(slug, true)
}

/** Première URL statique (Showdown dex). */
export function getStaticSpriteUrl(slug: string): string {
  return getShowdownStaticUrl(slug)
}

export function getStaticShinySpriteUrl(slug: string): string {
  return getShowdownStaticUrl(slug, true)
}

export function toShowdownSlug(nameEn: string): string {
  if (SPECIAL_SLUGS[nameEn]) {
    return SPECIAL_SLUGS[nameEn]
  }
  return nameEn
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/--+/g, '-')
    .replace(/^-|-$/g, '')
}

export function getPokeApiSpriteUrl(id: number, shiny = false): string {
  const path = shiny ? 'shiny/' : ''
  return `${POKEAPI_SPRITE}/${path}${id}.png`
}

function parseTyraDexFolderAndVariant(slug: string): { dexFolder: number; variantSuffix: string | null } | null {
  const paldeaTauros = TAUROS_PALDEA_TYRA_VARIANT[slug]
  if (paldeaTauros) {
    const dex = getIdForSlug('tauros')
    if (dex <= 0) return null
    return { dexFolder: dex, variantSuffix: paldeaTauros }
  }
  const parts = slug.split('-')
  const last = parts[parts.length - 1]
  if (parts.length >= 2 && last && REGION_SUFFIXES.has(last)) {
    const baseSlug = parts.slice(0, -1).join('-')
    const dex = getIdForSlug(baseSlug)
    if (dex <= 0) return null
    return { dexFolder: dex, variantSuffix: last }
  }
  let dex = getIdForSlug(slug)
  if (dex <= 0) {
    const alt = fixSlug(slug)
    if (alt !== slug) dex = getIdForSlug(alt)
  }
  if (dex <= 0) return null
  return { dexFolder: dex, variantSuffix: null }
}

export function getTyraDexSpriteUrlForSlug(slug: string, shiny = false): string {
  const parsed = parseTyraDexFolderAndVariant(slug)
  if (!parsed) return ''
  const { dexFolder, variantSuffix } = parsed
  const file =
    variantSuffix == null
      ? shiny
        ? 'shiny'
        : 'regular'
      : shiny
        ? `shiny_${variantSuffix}`
        : `regular_${variantSuffix}`
  return `${TYRA_DEX_BASE}/${dexFolder}/${file}.png`
}

export function getTyraDexSpriteUrl(pokedexId: number, shiny = false): string {
  const variant = shiny ? 'shiny' : 'regular'
  return `${TYRA_DEX_BASE}/${pokedexId}/${variant}.png`
}

export function getTrainerSpriteUrl(slug: string): string {
  return `${SHOWDOWN_BASE}/trainers/${fixSlug(slug)}.png`
}
